import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getContentFontStyle, getHeadingFontStyle } from '../../lib/contentBlockFontSize';
import { getIconFallbackLabel } from '../../lib/uiText';
import styles from './DescriptionModelo.module.css';

const VALID_TEXT_ALIGN = new Set(['left', 'center', 'right']);
const DESCRIPTION_MODELO_HEADING_FONT_SIZE_FALLBACKS = {
  h1: '2.25em',
  h2: '1.5em',
  h3: '1em',
};
const DESCRIPTION_MODELO_PARAGRAPH_FONT_SIZE = '0.95em';
const DESCRIPTION_MODELO_LIST_FONT_SIZE = '1rem';

const DescriptionModelo = ({ data, anchorId }) => {
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const { locale } = useGlobal();
  const iconMedia = data?.icon?.url
    ? getStrapiMediaAttributes(data.icon, {
      altFallback: getIconFallbackLabel(locale, 'section'),
      titleFallback: getIconFallbackLabel(locale, 'section'),
    })
    : null;
  const textAlign = VALID_TEXT_ALIGN.has(data?.textAlign) ? data.textAlign : 'left';

  const sectionClassName = [
    styles.descriptionModelo,
    styles[`descriptionModelo--align-${textAlign}`],
  ].filter(Boolean).join(' ');

  const renderContentBlock = (block, index) => {
    if (!block) return null;

    switch (block.__component) {
      case 'shared.heading': {
        const level = block.level || 'h2';
        const Tag = level;
        const headingStyle = getHeadingFontStyle(
          block?.fontSize,
          level,
          DESCRIPTION_MODELO_HEADING_FONT_SIZE_FALLBACKS
        );
        const headingClassName = [
          styles.descriptionModelo__heading,
          styles[`descriptionModelo__heading--${level}`],
          styles.descriptionModelo__animateItem,
        ].filter(Boolean).join(' ');

        return (
          <Tag key={`${level}-${index}`} className={headingClassName} style={headingStyle}>
            {block.text}
          </Tag>
        );
      }
      case 'shared.paragraph': {
        const paragraphStyle = getContentFontStyle(block?.fontSize, DESCRIPTION_MODELO_PARAGRAPH_FONT_SIZE);
        return (
          <p
            key={`paragraph-${index}`}
            className={[styles.descriptionModelo__text, styles.descriptionModelo__animateItem].join(' ')}
            style={paragraphStyle}
          >
            {block.text}
          </p>
        );
      }
      case 'shared.list': {
        const listItemStyle = getContentFontStyle(block?.fontSize, DESCRIPTION_MODELO_LIST_FONT_SIZE);
        const hasIcon = block.icon?.url;
        const listIconMedia = hasIcon
          ? getStrapiMediaAttributes(block.icon, {
            altFallback: getIconFallbackLabel(locale, 'list'),
            titleFallback: getIconFallbackLabel(locale, 'list'),
          })
          : null;
        const ListTag = !hasIcon && block.ordered ? 'ol' : 'ul';
        const listClassName = [
          styles.descriptionModelo__list,
          hasIcon ? styles['descriptionModelo__list--icons'] : null,
          !hasIcon && block.ordered ? styles['descriptionModelo__list--ordered'] : null,
        ].filter(Boolean).join(' ');

        return (
          <ListTag key={`list-${index}`} className={listClassName}>
            {block.items?.map((item, itemIndex) => (
              <li
                key={`list-item-${itemIndex}`}
                className={[styles.descriptionModelo__listItem, styles.descriptionModelo__animateItem].join(' ')}
                style={listItemStyle}
              >
                {listIconMedia?.src && (
                  <img
                    className={styles.descriptionModelo__listIcon}
                    src={listIconMedia.src}
                    alt={listIconMedia.alt}
                    title={listIconMedia.title}
                    aria-hidden="true"
                  />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ListTag>
        );
      }
      case 'shared.image': {
        const contentImageMedia = getStrapiMediaAttributes(block.image, {
          altFallback: block.alt || '',
          titleFallback: block.alt || '',
        });
        if (!contentImageMedia.src) return null;
        return (
          <img
            key={`image-${index}`}
            className={[styles.descriptionModelo__contentImage, styles.descriptionModelo__animateItem].join(' ')}
            src={contentImageMedia.src}
            alt={contentImageMedia.alt}
            title={contentImageMedia.title}
          />
        );
      }
      default:
        return null;
    }
  };

  useEffect(() => {
    const sectionEl = sectionRef.current;
    const contentEl = containerRef.current;
    if (!sectionEl || !contentEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const headingEls = contentEl.querySelectorAll(`.${styles.descriptionModelo__heading}`);
    const otherEls = contentEl.querySelectorAll(
      `.${styles.descriptionModelo__animateItem}:not(.${styles.descriptionModelo__heading})`
    );
    const iconEl = contentEl.querySelector(`.${styles.descriptionModelo__icon}`);

    gsap.set(sectionEl, { opacity: 0, y: 50 });
    gsap.set([...headingEls], { opacity: 0, y: 20 });
    gsap.set([...otherEls], { opacity: 0, y: 20 });
    if (iconEl) gsap.set(iconEl, { opacity: 0, y: 20 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;

          const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

          timeline.to(sectionEl, { opacity: 1, y: 0, duration: 0.6 });

          if (iconEl) {
            timeline.to(iconEl, { opacity: 1, y: 0, duration: 0.4 }, '-=0.2');
          }

          if (headingEls.length) {
            timeline.to(headingEls, { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }, '-=0.2');
          }

          if (otherEls.length) {
            timeline.to(otherEls, { opacity: 1, y: 0, duration: 0.5, stagger: 0.08 }, '-=0.15');
          }

          observer.disconnect();
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, []);

  if (!data) return null;

  return (
    <section ref={sectionRef} id={anchorId || undefined} className={sectionClassName}>
      <div ref={containerRef} className={styles.descriptionModelo__container}>
        {iconMedia?.src && (
          <img
            className={[styles.descriptionModelo__icon, styles.descriptionModelo__animateItem].join(' ')}
            src={iconMedia.src}
            alt={iconMedia.alt}
            title={iconMedia.title}
            aria-hidden="true"
          />
        )}
        <div className={styles.descriptionModelo__content}>
          {data.content?.map((block, index) => renderContentBlock(block, index))}
        </div>
      </div>
    </section>
  );
};

export default DescriptionModelo;